import type { Permission } from '@/constants/permissions';
import { DateTime } from 'luxon';

export interface OrganisationJson {
    public_id: string;
    name: string;
    logo: string | null;
    base_currency?: string | null;
    vat_number?: string | null;
    company_registration_number?: string | null;
    registered_office_address?: string | null;
    created_at: string;
    updated_at: string;
    oldest_order_date: string | null;
    permissions?: Permission[];
}

/**
 * The public id of the stand-in organisation, used before a navigation settles
 * a real one and when the user belongs to none.
 */
export const UNKNOWN_ORGANISATION = 'unknown';

/**
 * Whether this is an organisation the API can be asked about.
 *
 * Organisation-scoped queries are enabled on this rather than on a truthy
 * primary key, because the API does not expose primary keys -- see
 * `guardId`, which refuses the stand-in for the same reason.
 *
 * A plain function rather than a method: organisations are widely built as
 * object literals in tests, which would not carry one.
 */
export function isRealOrganisation(organisation: Organisation | undefined | null): boolean {
    return !!organisation && organisation.public_id !== UNKNOWN_ORGANISATION;
}

function parseDate(value: string | null, fallback: DateTime | null): DateTime | null {
    return value ? DateTime.fromISO(value) : fallback;
}

export class Organisation {
    public_id = 'unknown';
    name = 'Unknown';
    logo: string | null = null;
    base_currency: string | null = null;
    vat_number: string | null = null;
    company_registration_number: string | null = null;
    registered_office_address: string | null = null;
    created_at: DateTime = DateTime.now();
    updated_at: DateTime = DateTime.now();
    oldestOrderDate: DateTime | null = null;
    role?: string;
    // Optional because the class-field default is a lie: Object.create in
    // fromJSON skips initialisers, and the store's placeholder organisations
    // are plain literals. Readers must cope with it being absent.
    permissions?: Permission[];

    static fromJSON(json: OrganisationJson): Organisation {
        //console.debug("Organisation.fromJSON()", json);
        const user = Object.create(Organisation.prototype);
        return Object.assign(user, json, {
            created_at: parseDate(json.created_at, DateTime.now()),
            updated_at: parseDate(json.updated_at, DateTime.now()),
            oldestOrderDate: parseDate(json.oldest_order_date, null),
            // Object.create skips class-field initialisers, so the default on
            // the property above never runs here -- without this, permissions
            // is undefined on every hydrated organisation.
            permissions: json.permissions ?? [],
        });
    }

    /**
     * Whether the signed-in user may do this in this organisation. Absent
     * permissions deny -- see the note on the property.
     */
    hasPermission(permission: Permission): boolean {
        return this.permissions?.includes(permission) ?? false;
    }
}
